import { isPortableSetting, type StudyStoreMap, type StudyStoreName } from '@hoedokloop/domain';
import type { StudyFixture } from '@hoedokloop/domain/fixtures';

import { materializeStudyStoreValue } from './repository';

const studyStores: StudyStoreName[] = [
  'exams',
  'subjects',
  'studyLogs',
  'wrongNotes',
  'photoAssets',
  'plans',
  'planItems',
  'milestones',
  'mogakSessions',
  'mockScores',
  'noteInbox',
];

function values<Value>(database: IDBDatabase, store: string): Promise<Value[]> {
  if (!database.objectStoreNames.contains(store)) return Promise.resolve([]);
  return new Promise((resolve, reject) => {
    const request = database.transaction(store, 'readonly').objectStore(store).getAll();
    request.onsuccess = () => resolve(request.result as Value[]);
    request.onerror = () => reject(request.error);
  });
}

/** Open only an already-existing database, at its stored version, without Dexie upgrades. */
export async function readExistingStudySnapshot(name = 'hoedokloop'): Promise<StudyFixture> {
  if (!indexedDB.databases) throw new Error('Existing database inspection is unavailable');
  const known = await indexedDB.databases();
  if (!known.some((entry) => entry.name === name)) throw new Error('No existing study database');
  const database = await new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(name);
    request.onupgradeneeded = () => {
      request.transaction?.abort();
      reject(new Error('Recovery cannot create or upgrade a database'));
    };
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
  });
  try {
    const entries = await Promise.all(
      studyStores.map(async (store) => [
        store,
        (await values<StudyStoreMap[typeof store]>(database, store)).map((value) =>
          materializeStudyStoreValue(store, value),
        ),
      ]),
    );
    const userSettings = (
      await values<{ key: string; value: unknown; updatedAt: string }>(database, 'settings')
    ).filter((value) => isPortableSetting(value.key));
    return { ...Object.fromEntries(entries), userSettings } as StudyFixture;
  } finally {
    database.close();
  }
}
